import React, { useEffect, useMemo, useState } from 'react';
import Layout from '../components/Layout';
import PasswordChangeModal from '../components/PasswordChangeModal';
import { authService } from '../services/authService';

function toggleValue(list, value) {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
}

export default function ManageTeam() {
  const [user, setUser] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [members, setMembers] = useState([]);
  // Sectors/territories carry the catalogue's per-label `matches` count
  // straight through, so the picker can show a manager what a label
  // currently binds to. Role families have no data-side counterpart, so
  // they stay plain names.
  const [sectors, setSectors] = useState([]);
  const [territories, setTerritories] = useState([]);
  const [roleFamilies, setRoleFamilies] = useState([]);
  // The catalogue's vocabulary (Task 6): the real industries, functions and
  // countries a label's text can bind to, steering a manager off a typo
  // ("SG") or an off-vocabulary word ("Fintech") that matches nothing.
  const [vocab, setVocab] = useState({ industries: [], functions: [], countries: [] });
  const [selectedRecruiterId, setSelectedRecruiterId] = useState('');
  const [draft, setDraft] = useState({ sectors: [], territories: [], role_families: [] });
  const [newSectorName, setNewSectorName] = useState('');
  const [newTerritoryName, setNewTerritoryName] = useState('');
  const [newRoleFamilyName, setNewRoleFamilyName] = useState('');
  const [creating, setCreating] = useState({
    sector: false,
    territory: false,
    roleFamily: false,
  });

  const selectedMember = useMemo(
    () => members.find((m) => m.user_id === selectedRecruiterId) || null,
    [members, selectedRecruiterId]
  );

  useEffect(() => {
    loadUserData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedMember) {
      setDraft({ sectors: [], territories: [], role_families: [] });
      return;
    }
    setDraft({
      sectors: selectedMember.sectors || [],
      territories: selectedMember.territories || [],
      role_families: selectedMember.role_families || [],
    });
  }, [selectedMember]);

  const loadUserData = async () => {
    try {
      const userData = await authService.getCurrentUser();
      if (!userData) {
        window.location.href = '/login';
        return;
      }
      if (userData.role !== 'manager') {
        window.location.href = '/candidates';
        return;
      }
      setUser(userData);
      if (userData.must_change_password) {
        setShowPasswordModal(true);
      }
      await loadData();
    } catch (err) {
      setError(err.message || 'Failed to load user');
    } finally {
      setLoading(false);
    }
  };

  const loadData = async () => {
    setError('');
    const [teamResponse, sectorsData, territoriesData, roleFamilyData, vocabData] = await Promise.all([
      authService.getManagedTeamMembers(),
      authService.getSectors(),
      authService.getTerritories(),
      authService.getRoleFamilies(),
      authService.getScopeVocabulary().catch(() => null),
    ]);

    const teamMembers = teamResponse.members || [];
    setMembers(teamMembers);
    setSectors(sectorsData || []);
    setTerritories(territoriesData || []);
    setRoleFamilies((roleFamilyData || []).map((rf) => rf.name));
    if (vocabData) setVocab(vocabData);

    if (!selectedRecruiterId && teamMembers.length > 0) {
      setSelectedRecruiterId(teamMembers[0].user_id);
    } else if (selectedRecruiterId && !teamMembers.some((m) => m.user_id === selectedRecruiterId)) {
      setSelectedRecruiterId(teamMembers.length > 0 ? teamMembers[0].user_id : '');
    }
  };

  const handleSave = async () => {
    if (!selectedMember) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await authService.defineRecruiterSpecialisation(selectedMember.user_id, draft);
      await loadData();
      setSuccess(`Scope updated for ${selectedMember.display_name}.`);
    } catch (err) {
      setError(err.message || 'Failed to save recruiter scope');
    } finally {
      setSaving(false);
    }
  };

  const createAndSelectScopeValue = async (kind) => {
    const nameMap = {
      sector: newSectorName.trim(),
      territory: newTerritoryName.trim(),
      roleFamily: newRoleFamilyName.trim(),
    };
    const value = nameMap[kind];
    if (!value) return;

    setCreating((prev) => ({ ...prev, [kind]: true }));
    setError('');
    setSuccess('');

    try {
      if (kind === 'sector') {
        await authService.createSector(value);
        setDraft((prev) => ({ ...prev, sectors: prev.sectors.includes(value) ? prev.sectors : [...prev.sectors, value] }));
        setNewSectorName('');
      } else if (kind === 'territory') {
        await authService.createTerritory(value);
        setDraft((prev) => ({
          ...prev,
          territories: prev.territories.includes(value) ? prev.territories : [...prev.territories, value],
        }));
        setNewTerritoryName('');
      } else if (kind === 'roleFamily') {
        await authService.createRoleFamily(value);
        setDraft((prev) => ({
          ...prev,
          role_families: prev.role_families.includes(value) ? prev.role_families : [...prev.role_families, value],
        }));
        setNewRoleFamilyName('');
      }
      await loadData();
      setSuccess(`Added "${value}" to tenant scope options. It is now available to admin and managers in this tenant.`);
    } catch (err) {
      setError(err.message || `Failed to create ${kind}`);
    } finally {
      setCreating((prev) => ({ ...prev, [kind]: false }));
    }
  };

  const handlePasswordChanged = async () => {
    const updatedUserData = await authService.getCurrentUser();
    if (updatedUserData) {
      setUser(updatedUserData);
    }
  };

  if (loading || !user) return null;

  return (
    <Layout user={user}>
      {showPasswordModal && (
        <PasswordChangeModal
          user={user}
          onPasswordChanged={handlePasswordChanged}
          onClose={() => setShowPasswordModal(false)}
        />
      )}

      <div className="space-y-6">
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-2xl font-semibold text-gray-900">Manage Team</h2>
          <p className="text-sm text-gray-600 mt-2">
            Update scope only for recruiters assigned to you. Recruiters cannot access this page.
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-800 text-sm">
            {error}
          </div>
        )}
        {success && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-green-800 text-sm">
            {success}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="bg-white rounded-xl shadow-sm p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Assigned Recruiters</h3>
            {members.length === 0 ? (
              <p className="text-sm text-gray-500">No recruiters are currently assigned to you.</p>
            ) : (
              <div className="space-y-2">
                {members.map((member) => (
                  <button
                    key={member.user_id}
                    onClick={() => setSelectedRecruiterId(member.user_id)}
                    className={`w-full text-left p-3 rounded-lg border transition-colors ${
                      selectedRecruiterId === member.user_id
                        ? 'border-[#00C6AE] bg-[#EFFFFD]'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="font-medium text-gray-900">{member.display_name}</div>
                    <div className="text-xs text-gray-500">{member.email}</div>
                    <div className={`mt-2 text-xs ${member.is_configured ? 'text-green-700' : 'text-amber-700'}`}>
                      {member.is_configured ? 'Scope configured' : 'Scope incomplete'}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="lg:col-span-2 bg-white rounded-xl shadow-sm p-6">
            {!selectedMember ? (
              <p className="text-sm text-gray-500">Select a recruiter to edit their scope.</p>
            ) : (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{selectedMember.display_name}</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Daily list eligibility: {selectedMember.can_receive_daily_list ? 'Eligible' : 'Not eligible'}
                  </p>
                </div>

                <ScopePicker
                  title="Sectors"
                  options={sectors}
                  vocabulary={[...vocab.industries, ...vocab.functions]}
                  values={draft.sectors}
                  onToggle={(value) => setDraft((prev) => ({ ...prev, sectors: toggleValue(prev.sectors, value) }))}
                  newValue={newSectorName}
                  onNewValueChange={setNewSectorName}
                  onCreate={() => createAndSelectScopeValue('sector')}
                  creating={creating.sector}
                />

                <ScopePicker
                  title="Territories"
                  options={territories}
                  vocabulary={vocab.countries}
                  values={draft.territories}
                  onToggle={(value) => setDraft((prev) => ({ ...prev, territories: toggleValue(prev.territories, value) }))}
                  newValue={newTerritoryName}
                  onNewValueChange={setNewTerritoryName}
                  onCreate={() => createAndSelectScopeValue('territory')}
                  creating={creating.territory}
                />

                <ScopePicker
                  title="Role Families"
                  options={roleFamilies}
                  values={draft.role_families}
                  onToggle={(value) =>
                    setDraft((prev) => ({ ...prev, role_families: toggleValue(prev.role_families, value) }))
                  }
                  newValue={newRoleFamilyName}
                  onNewValueChange={setNewRoleFamilyName}
                  onCreate={() => createAndSelectScopeValue('roleFamily')}
                  creating={creating.roleFamily}
                />

                <div className="pt-2">
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="px-5 py-2 bg-[#00C6AE] text-white rounded-lg hover:bg-[#00B39E] disabled:opacity-50 transition-colors"
                  >
                    {saving ? 'Saving...' : 'Save Scope'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}

// `options` accepts either plain strings (role families, which have no
// data-side counterpart) or `{ name, matches }` records (sectors and
// territories) carrying the catalogue's live match count. `vocabulary` is
// the pool of real, canonical values the autocomplete suggests from --
// clicking a suggestion fills the exact string, never a re-cased one, since
// the text is what binds the label to data.
function ScopePicker({ title, options, vocabulary = [], values, onToggle, newValue, onNewValueChange, onCreate, creating }) {
  const q = newValue.trim().toLowerCase();
  const suggestions = q ? vocabulary.filter((v) => v.toLowerCase().includes(q)).slice(0, 8) : [];

  return (
    <div>
      <div className="flex items-center justify-between gap-2 mb-2">
        <h4 className="text-sm font-semibold text-gray-800">{title}</h4>
      </div>
      <div className="flex gap-2 mb-3">
        <input
          type="text"
          value={newValue}
          onChange={(e) => onNewValueChange(e.target.value)}
          placeholder={`Add new ${title.slice(0, -1).toLowerCase()}...`}
          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#00C6AE] focus:border-transparent"
        />
        <button
          onClick={onCreate}
          disabled={creating || !newValue.trim()}
          className="px-3 py-2 text-sm bg-gray-900 text-white rounded-lg hover:bg-gray-800 disabled:opacity-50 transition-colors"
        >
          {creating ? 'Adding...' : 'Add'}
        </button>
      </div>
      {suggestions.length > 0 && (
        <div className="mb-3 border border-gray-200 rounded-lg overflow-hidden">
          {suggestions.map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => onNewValueChange(v)}
              className="block w-full text-left px-3 py-1 text-sm hover:bg-gray-50"
            >
              {v}
            </button>
          ))}
        </div>
      )}
      {options.length === 0 ? (
        <p className="text-sm text-gray-500">No {title.toLowerCase()} available.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {options.map((opt) => {
            const name = typeof opt === 'string' ? opt : opt.name;
            const matches = typeof opt === 'string' ? null : opt.matches;
            return (
              <label key={name} className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={values.includes(name)}
                  onChange={() => onToggle(name)}
                  className="rounded border-gray-300 text-[#00C6AE] focus:ring-[#00C6AE]"
                />
                <span>{name}</span>
                {matches && (matches.companies + matches.people === 0 ? (
                  <span className="text-xs text-amber-700">0 records matched</span>
                ) : (
                  <span className="text-xs text-gray-500">
                    {matches.companies} companies · {matches.people} people
                  </span>
                ))}
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}
